import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useTrackedFlights, useSyncAllTrackedFlights } from '@/hooks/api-hooks';
import { setWebTitle, formatDay, formatToLocalTime, todayStart } from '@/lib/format-utils';
import { ServiceBooking, TrackedFlight } from '@turizi/shared';
import { Badge, Input, Button, Tooltip, SegmentedControl } from '@mantine/core';
import { BookingPaxsSize, SkeletonList } from '@/components/snippets';
import { IconSearch, IconPlaneInflight, IconRefresh, IconUsers, IconSofa, IconArmchair } from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router-dom';
import { DateInput } from '@mantine/dates';
import SmallBookingsList from '@/components/small-bookings-list';

// TODO: set this in the tenants settings
const HIGHLIGHT_AIRPORTS = ['BRC', 'CPC'];

export default function FlightsPage() {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const date = searchParams.get('date') || todayStart();

    const [searchQuery, setSearchQuery] = useState('');
    const [directionFilter, setDirectionFilter] = useState<'both' | 'arrivals' | 'departures'>('both');
    const {
        data,
        isLoading,
        refetch,
        isRefetching
    } = useTrackedFlights({ date });
    const syncMutation = useSyncAllTrackedFlights();
    const isSyncing = syncMutation.isPending || isRefetching;

    const handleRefresh = async () => {
        try {
            await syncMutation.mutateAsync();
        } catch (err) {
            console.error('Error syncing flights live:', err);
        }
        await refetch();
    };

    setWebTitle(t('flights'));

    const trackedFlights: TrackedFlight[] = data?.data || [];

    const filteredFlights = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        let list = trackedFlights;

        if (directionFilter === 'departures') {
            list = list.filter((f) => HIGHLIGHT_AIRPORTS.includes(f.origin_code || ''));
        } else if (directionFilter === 'arrivals') {
            list = list.filter((f) => HIGHLIGHT_AIRPORTS.includes(f.destination_code || ''));
        }

        if (query) {
            list = list.filter((f) =>
                (f.flight_number && f.flight_number.toLowerCase().includes(query)) ||
                (f.origin_code && f.origin_code.toLowerCase().includes(query)) ||
                (f.destination_code && f.destination_code.toLowerCase().includes(query)) ||
                (f.status && f.status.toLowerCase().includes(query)) ||
                (f.fa_flight_id && f.fa_flight_id.toLowerCase().includes(query))
            );
        }
        const getFlightMs = (f: TrackedFlight) => {
            const isArr = HIGHLIGHT_AIRPORTS.includes(f.destination_code || '');
            const isDep = HIGHLIGHT_AIRPORTS.includes(f.origin_code || '');
            let timeStr: string | null | undefined;
            if (isArr && !isDep) {
                timeStr = f.actual_in || f.estimated_in || f.scheduled_in || f.actual_out || f.estimated_out || f.scheduled_out;
            } else {
                timeStr = f.actual_out || f.estimated_out || f.scheduled_out || f.actual_in || f.estimated_in || f.scheduled_in;
            }
            return timeStr ? new Date(timeStr).getTime() : 0;
        };

        return [...list].sort((a, b) => {
            const msA = getFlightMs(a);
            const msB = getFlightMs(b);
            if (msA !== msB) return msA - msB;
            return (a.flight_number || '').localeCompare(b.flight_number || '');
        });
    }, [trackedFlights, searchQuery, directionFilter]);

    const getStatusColor = (status: string | null) => {
        if (!status) return 'gray';
        const lower = status.toLowerCase();
        if (lower.includes('cancel')) return 'red';
        if (lower.includes('arrived') || lower.includes('landed')) return 'green';
        if (lower.includes('delay') || lower.includes('late')) return 'orange';
        if (lower.includes('route') || lower.includes('en route') || lower.includes('on time')) return 'blue';
        if (lower.includes('sched')) return 'gray';
        return 'yellow';
    };

    const getRowStatusBg = (status: string | null, isPast = false) => {
        const defaultBg = isPast ? 'bg-gray-200' : 'bg-white';
        if (!status) return defaultBg;
        const lower = status.toLowerCase();
        if (lower.includes('cancel')) return 'bg-red-100/90';
        if (lower.includes('arrived') || lower.includes('landed')) return 'bg-green-100/90';
        return defaultBg;
    };

    const highlightAirport = (code?: string) => {
        return HIGHLIGHT_AIRPORTS.includes(code || '') ? 'font-extrabold' : 'font-light text-gray-600';
    };

    const calculateTotalPax = (bookings: ServiceBooking[]): number => bookings?.reduce((sum, b) => sum + BookingPaxsSize(b), 0) || 0;

    const renderFlightTable = (flights: TrackedFlight[], isPast = false) => {
        if (flights.length === 0) return null;

        return (
            <div className="flex flex-col gap-3 w-full">
                {flights.map((flight) => {
                    const bookings = (flight as any).service_bookings || [];
                    const totalPax = calculateTotalPax(bookings);
                    const lastCheckedRaw = flight.last_checked_at || flight.updated_at;
                    const lastChecked = lastCheckedRaw
                        ? {
                            fullDate: dayjs(lastCheckedRaw).locale(i18n.language).format('DD/MM/YYYY HH:mm'),
                            label: dayjs(lastCheckedRaw).locale(i18n.language).fromNow(),
                            isFresh: dayjs().diff(dayjs(lastCheckedRaw), 'minute') < 60,
                        }
                        : null;

                    const originTz = (flight as any).raw_data?.origin?.timezone;
                    const destTz = (flight as any).raw_data?.destination?.timezone;

                    const isDepHighlighted = HIGHLIGHT_AIRPORTS.includes(flight.origin_code || '');
                    const isArrHighlighted = HIGHLIGHT_AIRPORTS.includes(flight.destination_code || '');

                    const depScheduled = flight.scheduled_out;
                    const depEstimated = flight.estimated_out;
                    const depActual = flight.actual_out;
                    const depActualOrEst = depActual || depEstimated;
                    const depTime = depActualOrEst || depScheduled;

                    const isDepChanged = !!(depScheduled && depActualOrEst && Math.abs(new Date(depActualOrEst).getTime() - new Date(depScheduled).getTime()) >= 60 * 1000);

                    const arrScheduled = flight.scheduled_in;
                    const arrEstimated = flight.estimated_in;
                    const arrActual = flight.actual_in;
                    const arrActualOrEst = arrActual || arrEstimated;
                    const arrTime = arrActualOrEst || arrScheduled;

                    const isArrChanged = !!(arrScheduled && arrActualOrEst && Math.abs(new Date(arrActualOrEst).getTime() - new Date(arrScheduled).getTime()) >= 60 * 1000);

                    return (
                        <div
                            key={flight.id}
                            className={`border border-gray-200 rounded-xl p-3 shadow-2xs ${getRowStatusBg(flight.status, isPast)} transition-colors w-full`}
                        >
                            <div className="flex flex-col lg:flex-row lg:items-start gap-3 justify-between">
                                {/* Operational Flight Info: Flight #, Route, Times, Pax Count */}
                                <div className="flex flex-wrap items-start gap-3 shrink-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`font-black text-base ${isPast ? 'text-gray-700' : 'text-gray-900'}`}>
                                            {flight.flight_number}
                                        </span>
                                        {/* not usefull to user */}
                                        {/* {flight.fa_flight_id && (
                                            <Tooltip label={`Flight ID: ${flight.fa_flight_id}`} withArrow>
                                                <span className="text-xxs font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded cursor-help border border-gray-200">
                                                    {flight.fa_flight_id.split('-')[0]}
                                                </span>
                                            </Tooltip>
                                        )} */}
                                    </div>

                                    {/* Route badge */}
                                    <div className="inline-flex items-center gap-2 px-2.5 py-0.5 bg-slate-100 rounded-lg border border-slate-200 text-xs font-bold text-slate-800">
                                        <span className={highlightAirport(flight.origin_code)}>{flight.origin_code || '---'}</span>
                                        <span className="text-blue-500">➔</span>
                                        <span className={highlightAirport(flight.destination_code)}>{flight.destination_code || '---'}</span>
                                    </div>

                                    {/* Departure (OUT) */}
                                    <div
                                        className={`flex flex-col text-xs 
                                            ${isDepHighlighted
                                                ? 'bg-black text-white border border-gray-300 px-2 py-0.5 rounded-lg shadow-sm'
                                                : ''}
                                            `}>
                                        {isDepChanged ? (
                                            <Tooltip
                                                label={`${t('scheduled_short')}: ${formatToLocalTime(depScheduled, originTz)} | ${depActual ? t('actual_short') : t('projected_short')}: ${formatToLocalTime(depActualOrEst, originTz)}`}
                                                withArrow
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`font-black ${isDepHighlighted ? 'text-white' : 'text-gray-900'}`}>
                                                        {formatToLocalTime(depActualOrEst, originTz)}
                                                    </span>
                                                    <span className=" text-gray-400 text-xxs">
                                                        {formatToLocalTime(depScheduled, originTz)}
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <span className={`font-black ${isDepHighlighted ? 'text-white' : 'text-gray-900'}`}>
                                                {depTime ? formatToLocalTime(depTime, originTz) : '--:--'}
                                            </span>
                                        )}
                                        {depTime && (
                                            <span className="text-xxs text-gray-500">
                                                {formatDay(depTime, originTz)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Arrival (IN) */}
                                    <div
                                        className={`
                                            flex flex-col text-xs 
                                            ${isArrHighlighted
                                                ? 'bg-black text-white border border-gray-300 px-2 py-0.5 rounded-lg shadow-sm'
                                                : ''}
                                            `}>
                                        {isArrChanged ? (
                                            <Tooltip
                                                label={`${t('scheduled_short')}: ${formatToLocalTime(arrScheduled, destTz)} | ${arrActual ? t('actual_short') : t('projected_short')}: ${formatToLocalTime(arrActualOrEst, destTz)}`}
                                                withArrow
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`font-black ${isArrHighlighted ? 'text-white' : 'text-gray-900'}`}>
                                                        {formatToLocalTime(arrActualOrEst, destTz)}
                                                    </span>
                                                    <span className=" text-gray-400 text-xxs">
                                                        {formatToLocalTime(arrScheduled, destTz)}
                                                    </span>
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <span className={`font-black ${isArrHighlighted ? 'text-white' : 'text-gray-900'}`}>
                                                {arrTime ? formatToLocalTime(arrTime, destTz) : '--:--'}
                                            </span>
                                        )}
                                        {arrTime && (
                                            <span className="text-xxs text-gray-500">
                                                {formatDay(arrTime, destTz)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Total Pax Badge */}
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100/90 border border-slate-200 rounded-lg text-md font-extrabold text-slate-800">
                                        <IconArmchair size={15} className='text-gray-400' />
                                        <span>{totalPax}</span>
                                    </div>
                                </div>

                                {/* Middle: Associated Bookings List */}
                                <div className="flex-1 min-w-0">
                                    <SmallBookingsList
                                        bookings={bookings}
                                    />
                                </div>

                                {/* Far Right End: Flight Status & Last Sync stacked */}
                                <div className="flex flex-col items-end gap-1.5 shrink-0 ml-auto">
                                    <Badge color={getStatusColor(flight.status)} variant="light" size="sm">
                                        {flight.status || '-'}
                                    </Badge>

                                    {lastChecked && (
                                        <Tooltip label={`${t('last_checked')}: ${lastChecked.fullDate}`} withArrow>
                                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-50 rounded-md border border-gray-200 text-xxs text-gray-600 font-medium cursor-help">
                                                <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${lastChecked.isFresh ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                                                <span>{lastChecked.label}</span>
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="panel-content space-y-2">
            {/* Header */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                        <IconPlaneInflight size={26} />
                    </div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 m-0">{t('flights')}</h1>
                        <p className="text-xs text-gray-500 m-0 mt-0.5">{t('tracked_flights_description')}</p>
                    </div>
                </div>
                <Button
                    variant="light"
                    color="blue"
                    size="xs"
                    leftSection={<IconRefresh size={14} className={isSyncing ? 'animate-spin' : ''} />}
                    onClick={handleRefresh}
                    disabled={isSyncing}
                >
                    {t('refresh')}
                </Button>
            </div>

            {/* Controls Bar */}
            <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    <DateInput
                        locale={i18n.language}
                        className='font-bold'
                        highlightToday={true}
                        value={date}
                        onChange={(newDate) => {
                            setSearchParams({ date: newDate || todayStart() });
                        }}
                    />
                    <SegmentedControl
                        size="md"
                        color='black'
                        radius="md"
                        value={directionFilter}
                        onChange={(val) => setDirectionFilter(val as 'both' | 'arrivals' | 'departures')}
                        data={[
                            { label: t('filter_both'), value: 'both' },
                            { label: t('IN'), value: 'arrivals' },
                            { label: t('OUT'), value: 'departures' },
                        ]}
                    />
                </div>
                <div className="flex-1 min-w-[240px]">
                    <Input
                        placeholder={t('search_tracked_flights_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        leftSection={<IconSearch size={15} className="text-gray-400" />}
                        size="sm"
                    />
                </div>
            </div>

            {/* Content Sections */}
            {isLoading ? (
                <SkeletonList />
            ) : filteredFlights.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-xl text-center gap-2">
                    <IconPlaneInflight size={40} className="text-gray-300" />
                    <span className="font-bold text-gray-700 text-sm">{t('no_tracked_flights_found')}</span>
                    <span className="text-xs text-gray-500 max-w-md">{t('no_tracked_flights_help')}</span>
                </div>
            ) : (
                <div>
                    {renderFlightTable(filteredFlights, false)}
                </div>
            )}
        </div>
    );
}
